import { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router';

import { Button } from '@/components/Button';
import { steps } from './constants';
import { defaultFormData, useEmailAssistantStore } from '@/store/useEmailAssistantStore';
import { LuArrowLeft } from 'react-icons/lu';
import { Stepper } from '@/components/Stepper';
import { aiEmailAssistantSchema, type AiEmailAssistantForm } from '@/schemas/ai-email-assistant';

export default function AiEmailAssistantPage() {
  const [currentStep, setCurrentStep] = useState(0);
  const { formData, setFormData, resetForm } = useEmailAssistantStore();
  const navigate = useNavigate();

  const methods = useForm<AiEmailAssistantForm>({
    resolver: zodResolver(aiEmailAssistantSchema),
    defaultValues: formData
  });

  const { handleSubmit, trigger } = methods;
  const StepContent = steps[currentStep].content;
  const isLastStep = currentStep === steps.length - 1;

  const onSubmit = async (data: AiEmailAssistantForm) => {
    setFormData(data);
    navigate(`/ai-email-assistant/generated-email`);
  };

  const onClickNext = async () => {
    if (isLastStep) {
      handleSubmit(onSubmit)();
      return;
    }

    const fieldsToValidate = steps[currentStep].fields as (keyof AiEmailAssistantForm)[];
    const isValid = await trigger(fieldsToValidate);

    if (isValid) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleClickBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    } else {
      navigate('/dashboard');
    }
  };

  useEffect(() => {
    methods.reset(defaultFormData);
  }, [methods.reset]);

  return (
    <FormProvider {...methods}>
      <div className="h-[100vh] flex items-center justify-center">
        <div className="flex flex-col justify-center gap-8 bg-jacarta-400 rounded-xl py-8 px-16 max-w-[1070px] w-full mx-auto">
          <div>
            <button
              type="button"
              onClick={handleClickBack}
              className="mb-3 flex items-center gap-1 hover:bg-white/20 transition-background duration-200 text-white p-2 rounded-md"
            >
              <LuArrowLeft size={22} /> Back to {currentStep > 0 ? 'previous step' : 'dashboard'}
            </button>

            <h2 className="text-3xl	text-white font-semibold text-start">Tivy Email Assistant</h2>
          </div>

          <Stepper steps={steps} activeStep={currentStep} />

          <form>
            <StepContent />

            <Button
              color="green"
              type="button"
              onClick={onClickNext}
              className="w-80 block mx-auto mt-8"
              label={isLastStep ? 'Submit' : 'Continue'}
            />
          </form>
        </div>
      </div>
    </FormProvider>
  );
}
